/**
 * 카카오 알림톡 템플릿 변수/버튼 구조 헬퍼 (클라이언트측 순수 로직).
 *
 * 참고 소스(twomos Marketing/Template/kakaoTemplate.ts) 패리티를 에버라인 타입으로 이식.
 * ★ 변수 토큰은 BE 정본 `#{var}` 형식(be-notif 공표 + `AlimtalkTemplateRenderer` PLACEHOLDER
 *   `#\{([A-Za-z0-9_]+)}` + 시드 템플릿 `#{certificationCode}` 등). 키는 영문/숫자/언더스코어만.
 * 계약(be-notif 공표):
 *  - variablesJson: [{ key, example }] JSON 문자열 (기존 에버라인 템플릿 contract 유지).
 *  - buttons: 구조화 배열 [{ name, type(WL|AL|DS|BK|MD), urlMobile, urlPc }] (buttonsJson 문자열 아님).
 *  - 이미지형 imageUrl: 별도 String 필드(be Task#8 additive 예정) — buttonsJson 운반 패턴 폐기.
 */
import type { TFunction } from 'i18next';
import type {
  KakaoButtonType,
  KakaoTemplateType,
  TemplateButton,
} from '../types/notificationTemplate';

export type { KakaoButtonType, TemplateButton };

/**
 * 변수 토큰: `#{var}` (BE 정본 — `AlimtalkTemplateRenderer` PLACEHOLDER `#\{([A-Za-z0-9_]+)}`,
 * 시드 템플릿 `#{certificationCode}` 등). be-notif 공표 포맷 정합. 키는 영문/숫자/언더스코어만.
 */
const VARIABLE_TOKEN_RE = /#\{([A-Za-z0-9_]+)\}/g;

/** 변수 키 허용 문자: 영문/숫자/언더스코어(BE 렌더러 PLACEHOLDER 와 동일). */
const VARIABLE_KEY_RE = /^[A-Za-z0-9_]+$/;

/** 변수 최대 개수(카카오 정책). */
export const MAX_VARIABLES = 40;

/** 강조표기 제목 입력 최대 길이. */
export const EMPHASIS_TITLE_MAX_LENGTH = 50;

/** 강조표기 부제 입력 최대 길이. */
export const EMPHASIS_SUBTITLE_MAX_LENGTH = 18;

export const KAKAO_TEMPLATE_TYPES: ReadonlyArray<KakaoTemplateType> = [
  'basic',
  'emphasis',
  'image',
];

/** BE 응답값 → 템플릿형. 미지정/미지원 값은 기본형으로 수렴. */
export const normalizeKakaoTemplateType = (
  value: string | null | undefined,
): KakaoTemplateType => {
  const v = (value ?? '').toLowerCase();
  return v === 'emphasis' || v === 'image' ? v : 'basic';
};

/** 폼에서 다루는 변수 항목. */
export interface TemplateVariable {
  key: string;
  example: string;
}

/** 버튼 타입(BE TemplateButtonView.type): 웹링크/앱링크/배송조회/봇키워드/메시지전달. */
export const KAKAO_BUTTON_TYPES: ReadonlyArray<KakaoButtonType> = ['WL', 'AL', 'DS', 'BK', 'MD'];

export const createEmptyButton = (): TemplateButton => ({
  name: '',
  type: 'WL',
  urlMobile: null,
  urlPc: null,
});

/** URL 이 필수인 버튼 타입(웹링크/앱링크). 그 외(DS/BK/MD)는 name 만. */
const URL_REQUIRED_TYPES: ReadonlyArray<KakaoButtonType> = ['WL', 'AL'];

/** content 에서 #{변수} 토큰을 순서대로(중복 제거) 추출. */
export const extractVariableKeys = (content: string): string[] => {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const match of content.matchAll(VARIABLE_TOKEN_RE)) {
    const key = (match[1] ?? '').trim();
    if (key && !seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }
  return keys;
};

/** content 토큰과 기존 변수 목록을 동기화(기존 example 보존, 사라진 키 제거). */
export const syncVariables = (
  content: string,
  existing: ReadonlyArray<TemplateVariable>,
): TemplateVariable[] => {
  const exampleByKey = new Map(existing.map((v) => [v.key, v.example]));
  return extractVariableKeys(content).map((key) => ({
    key,
    example: exampleByKey.get(key) ?? '',
  }));
};

/** 본문의 #{변수}를 예시값으로 치환한 미리보기 텍스트(예시값 없으면 토큰 유지). */
export const renderPreview = (
  content: string,
  variables: ReadonlyArray<TemplateVariable>,
): string => {
  const exampleByKey = new Map(variables.map((v) => [v.key, v.example]));
  return content.replace(VARIABLE_TOKEN_RE, (whole, rawKey: string) => {
    const example = exampleByKey.get(rawKey.trim());
    return example ? example : whole;
  });
};

export interface TemplateValidationResult {
  errors: string[];
  warnings: string[];
}

export interface TemplateDesignInfo {
  kakaoTemplateType: KakaoTemplateType;
  emphasisTitle: string;
  emphasisSubtitle: string;
  imageUrl?: string;
}

type TFn = TFunction<'notifications', undefined>;

/**
 * 알림톡 템플릿 변수/버튼/템플릿형 검증. errors=저장 차단, warnings=안내.
 * i18n t 를 받아 한글 메시지(라벨 SSoT)로 반환.
 */
export const validateKakaoTemplate = (
  t: TFn,
  variables: ReadonlyArray<TemplateVariable>,
  buttons: ReadonlyArray<TemplateButton>,
  design?: TemplateDesignInfo,
): TemplateValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (variables.length > MAX_VARIABLES) {
    errors.push(t('template.variables.max', '변수는 최대 {{count}}개까지 사용할 수 있습니다.', { count: MAX_VARIABLES }));
  }

  variables.forEach((v) => {
    if (!VARIABLE_KEY_RE.test(v.key) || !v.example.trim()) {
      errors.push(
        t('template.validate.variableExampleRequired', '변수 "{{name}}"의 예시 값을 입력하세요.', { name: v.key }),
      );
    }
  });

  buttons.forEach((b, i) => {
    const nameMissing = !b.name.trim();
    const urlMissing = URL_REQUIRED_TYPES.includes(b.type) && !(b.urlMobile ?? '').trim();
    if (nameMissing || urlMissing) {
      errors.push(
        t('template.validate.buttonRequired', '{{index}}번 버튼의 필수 항목을 입력하세요.', { index: i + 1 }),
      );
    }
  });

  if (design?.kakaoTemplateType === 'emphasis') {
    if (!design.emphasisTitle.trim()) {
      errors.push(t('template.validate.emphasisTitleRequired', '강조표기형은 강조 제목이 필요합니다.'));
    }
    if (!design.emphasisSubtitle.trim()) {
      errors.push(t('template.validate.emphasisSubtitleRequired', '강조표기형은 강조 부제가 필요합니다.'));
    }
  }

  return { errors, warnings };
};

/** 변수 목록 → variablesJson 문자열(빈 배열이면 null). */
export const toVariablesJson = (variables: ReadonlyArray<TemplateVariable>): string | null =>
  variables.length > 0
    ? JSON.stringify(variables.map((v) => ({ key: v.key, example: v.example })))
    : null;

/** 발송/저장용 버튼 정규화 — 빈 name 제거, 미사용 URL null 화(BE TemplateButton 형태). */
export const sanitizeButtons = (buttons: ReadonlyArray<TemplateButton>): TemplateButton[] =>
  buttons
    .filter((b) => b.name.trim().length > 0)
    .map((b) => ({
      name: b.name.trim(),
      type: b.type,
      urlMobile: b.urlMobile?.trim() ? b.urlMobile.trim() : null,
      urlPc: b.urlPc?.trim() ? b.urlPc.trim() : null,
    }));

/** variablesJson 파싱 → 변수 목록(구버전 string[] 도 수용). */
export const parseVariablesJson = (json: string | null | undefined): TemplateVariable[] => {
  if (!json) return [];
  try {
    const parsed: unknown = JSON.parse(json);
    if (!Array.isArray(parsed)) {
      // 구버전 object map {key:value} 도 수용.
      if (parsed && typeof parsed === 'object') {
        return Object.entries(parsed as Record<string, unknown>).map(([key, value]) => ({
          key,
          example: value == null ? '' : String(value),
        }));
      }
      return [];
    }
    return parsed
      .map((item): TemplateVariable | null => {
        if (typeof item === 'string') return { key: item.replace(/^#\{|\}$/g, '').trim(), example: '' };
        if (item && typeof item === 'object') {
          const rec = item as Record<string, unknown>;
          if (typeof rec.key === 'string') {
            return { key: rec.key, example: typeof rec.example === 'string' ? rec.example : '' };
          }
        }
        return null;
      })
      .filter((v): v is TemplateVariable => v !== null);
  } catch {
    return [];
  }
};

/** BE wire 버튼(구조화) → 폼 버튼. 미지원 type 은 WL 로 수렴. */
export const fromTemplateButtons = (
  buttons: ReadonlyArray<{ name?: string | null; type?: string | null; urlMobile?: string | null; urlPc?: string | null }> | null | undefined,
): TemplateButton[] => {
  if (!buttons) return [];
  return buttons.map((b) => ({
    name: b.name ?? '',
    type: (KAKAO_BUTTON_TYPES as ReadonlyArray<string>).includes(b.type ?? '') ? (b.type as KakaoButtonType) : 'WL',
    urlMobile: b.urlMobile ?? null,
    urlPc: b.urlPc ?? null,
  }));
};
